import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Coffee } from 'lucide-react';
import { api } from '../../services/api';
import type { ActiveNowEntry } from '../../types';
import DataTable from '../DataTable';
import EmptyState from '../EmptyState';
import { formatTime } from '../../utils/format';
import { runningClock } from './workLogsPageModel';
import { useNow } from './RunningNowBlock';

/**
 * P-10d (D-72) — "פעילות עכשיו" (manager tab): who is working right now, on
 * what, and since when, with the running time. Every running timer, from
 * GET /work-logs/active-now; refreshed every 30 seconds.
 */
export default function ActiveNowPanel() {
  const { t } = useTranslation();
  const now = useNow();
  const q = useQuery({
    queryKey: ['work-logs', 'active-now'],
    queryFn: () => api.getWorkLogsActiveNow(),
    refetchInterval: 30_000,
    staleTime: 0,
  });
  const rows = (q.data?.data ?? []) as ActiveNowEntry[];
  const employees = new Set(rows.map((r) => r.user_id)).size;

  return (
    <section aria-labelledby="wl-active-now-title" className="space-y-3">
      <h2 id="wl-active-now-title" className="text-sm text-text-2">
        {q.isLoading ? t('workLogs.activeNow.loading') : t('workLogs.activeNow.summary', { count: employees })}
      </h2>
      <DataTable
        columns={[
          {
            key: 'agent',
            header: t('workLogs.workerColumn'),
            render: (r: ActiveNowEntry) => <span className="font-medium text-text">{r.agent_name || '—'}</span>,
          },
          {
            key: 'task',
            header: t('workLogs.activeNow.colTask'),
            link: true,
            render: (r: ActiveNowEntry) => <bdi>{r.ticket_title || t('workLogs.noTitle')}</bdi>,
            sub: (r: ActiveNowEntry) => (r.organization_name ? <span className="text-xs text-text-2">{r.organization_name}</span> : null),
          },
          {
            key: 'since',
            header: t('workLogs.activeNow.colSince'),
            align: 'end',
            width: 'w-28',
            render: (r: ActiveNowEntry) => <span className="whitespace-nowrap tabular-nums text-text-2">{formatTime(r.started_at)}</span>,
          },
          {
            key: 'elapsed',
            header: t('workLogs.activeNow.colElapsed'),
            align: 'end',
            width: 'w-28',
            render: (r: ActiveNowEntry) => (
              <span className="whitespace-nowrap font-semibold tabular-nums text-text"><bdi dir="ltr">{runningClock(r.started_at, now)}</bdi></span>
            ),
          },
        ]}
        data={rows}
        keyField="id"
        caption={t('workLogs.activeNow.caption')}
        density="compact"
        mobile="cards"
        getRowHref={(r: ActiveNowEntry) => `/tickets/${r.ticket_id}?from=/work-logs`}
        rowLabel={(r: ActiveNowEntry) => `${r.agent_name ?? ''} · ${r.ticket_title ?? ''}`}
        isLoading={q.isLoading}
        skeletonRows={4}
        error={q.isError && !q.data ? q.error : undefined}
        errorMessage={t('workLogs.activeNow.loadError')}
        onRetry={() => void q.refetch()}
        emptyState={<EmptyState icon={Coffee} title={t('workLogs.activeNow.emptyTitle')} description={t('workLogs.activeNow.emptyHint')} />}
      />
    </section>
  );
}
